import React, { useState, useEffect, useRef } from "react";
import { useLocation, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import PageLayout from "../../components/PageLayout";
import TransactionTypePicker from "./TransactionTypePicker";
import QuoteDetailsPage from "./QuoteDetailsPage";
import QuoteSubmission from "./QuoteSubmission";
import QuoteSorryPage from "../QuoteCommon/QuoteSorryPage";
import QuoteBuyingProperty from "../QuoteCommon/QuoteBuyingProperty";
import { selectGAClientId } from "../../features/analytics/analyticsSlice";
import useCampaignAttribution from "../../hooks/useCampaignAttribution";

import { parseTransactionType, userSkippedTransactionTypePicker } from "../../utils/queryStringUtils";
import StepFlowNavigate from "../../components/StepFlowNavigate";
import SaveQuoteForm from "./SaveQuoteForm";
import AdsConversionStep from "./AdsConversionStep";
import QuoteSellingProperty from "../QuoteCommon/QuoteSellingProperty";
import { BuyingMortgageLender } from "../QuoteCommon/MortgageLender";

// "Sign up now" leaves the quote page, so Back from the sign-up page reloads
// it. Keep the shown quote for this tab so that shows the quote again, rather
// than an empty form (and a second lead if they re-quote).
const SHOWN_QUOTE_KEY = "juno.shownQuote";

const restoreShownQuote = (location) => {
  if (new URLSearchParams(location.search).get("step") !== "your-quote") return null;
  try {
    const saved = JSON.parse(window.sessionStorage.getItem(SHOWN_QUOTE_KEY));
    return saved?.pathname === location.pathname ? saved.quote : null;
  } catch {
    return null;
  }
};

const saveShownQuote = (location, quote) => {
  try {
    window.sessionStorage.setItem(SHOWN_QUOTE_KEY, JSON.stringify({ pathname: location.pathname, quote }));
  } catch {
    // Not saving only loses the quote on Back.
  }
};

const QuoteFlow = (props) => {
  const { referralCode } = useParams();
  const location = useLocation();
  const [submittedFlowState, setSubmittedFlowState] = useState(() => restoreShownQuote(location));
  const [flowCompleted, setFlowCompleted] = useState(submittedFlowState != null);

  // FIXME: move this into components that use it once they've been converted
  // to functional components.
  const googleAnalyticsId = useSelector(selectGAClientId);
  const campaign = useCampaignAttribution();

  const [state, setState] = useState({
    loading: false,
    error: null,
    referral: null,
    referralLocked: false,
    defaultChannel: "juno.legal",
    defaultReferrer: null,
    // A ?transaction= value that matches no property form (a withdrawn type,
    // a typo) is treated as no transaction so the picker shows, instead of
    // skipping straight to personal details with no property data.
    transactionType: parseTransactionType(location),
  });

  const lastFetchedReferralCode = useRef(null);

  useEffect(() => {
    if (!flowCompleted && !!referralCode) {
      if (lastFetchedReferralCode.current === referralCode) return;
      lastFetchedReferralCode.current = referralCode;

      setState((prevState) => ({ ...prevState, loading: true, referralLocked: true }));

      fetch("/api/referralCode/" + referralCode)
        .then((resp) => resp.json())
        .then(
          (referralData) => {
            if (referralData.referralCodeValid) {
              const referral = {
                referralCode: referralCode,
                discountAmount: referralData.discountAmount,
                discountMessage: referralData.discountMessage,
                quoteMessage: referralData.quoteMessage,
                referralSource: referralData.referralSource,
              };

              setState((prevState) => ({ ...prevState, loading: false, referral }));
            } else {
              if (process.env.NODE_ENV === "development") {
                console.warn(
                  `DEV: referral code "${referralCode}" not found. Referral codes are synced from webapp-alpha. ` +
                    "Run ./scripts/run_manage_command.sh resync_referral_codes in webapp-alpha to populate them locally.",
                );
              }
              setState((prevState) => ({
                ...prevState,
                loading: false,
                referral: null,
                defaultChannel: "estate_agent_referrals",
                defaultReferrer: referralCode,
              }));
            }
          },
          (err) => {
            console.error("Failed to load referral code", err);
            setState((prevState) => ({ ...prevState, loading: false }));
          },
        );
    }
  }, [referralCode, flowCompleted]);

  const shouldShowTransactionPicker = () => {
    return state.transactionType === null;
  };

  const shouldShowSellingQuoteDetailsForm = (transactionType) => {
    return transactionType === "Selling" || transactionType === "BuyingAndSelling";
  };

  const shouldShowBuyingQuoteDetailsForm = (transactionType) => {
    return transactionType === "Buying" || transactionType === "BuyingAndSelling";
  };

  const shouldShowBuyingMortgageLender = (transactionDetails) => {
    return transactionDetails && !!transactionDetails.buyToLet;
  };

  if (state.loading) {
    return null;
  }

  const pageWidth = "wider";

  if (flowCompleted) {
    return (
      <PageLayout pageWidth={pageWidth}>
        <QuoteDetailsPage
          pageWidth={pageWidth}
          referral={submittedFlowState?.referral}
          buyingDetails={submittedFlowState?.buyingDetails}
          sellingDetails={submittedFlowState?.sellingDetails}
          transactionType={submittedFlowState.transactionType}
          quoteDetails={submittedFlowState.quoteDetails}
        />
      </PageLayout>
    );
  }

  return (
    <PageLayout pageWidth={pageWidth}>
      <StepFlowNavigate
        initialFlowState={{
          transactionType: state.transactionType,
          personalDetails: null,
          sellingDetails: null,
          sellingRejectionReasons: [],
          buyingDetails: null,
          buyingRejectionReasons: [],
          referral: state.referral,
        }}
        steps={[
          {
            name: "Transaction type",
            slug: "get-a-quote",
            valid: () => shouldShowTransactionPicker(),
            component: (flowState) => (
              <TransactionTypePicker
                transactionType={flowState.transactionType}
                referral={flowState.referral}
                pageWidth={pageWidth}
              />
            ),
          },
          {
            name: "Selling Quote details",
            slug: "selling-quote",
            isFirstPage: (flowState) => ["Selling", "BuyingAndSelling"].includes(flowState.transactionType),
            valid: (flowState) => shouldShowSellingQuoteDetailsForm(flowState.transactionType),
            component: (flowState) => (
              <AdsConversionStep key="selling-quote" step="selling-quote">
                <QuoteSellingProperty
                  mode="direct"
                  transactionType={flowState.transactionType}
                  sellingDetails={flowState.sellingDetails}
                  defaultChannel={state.defaultChannel}
                  referral={flowState.referral}
                  referralLocked={state.referralLocked}
                  showLandingCopy={
                    userSkippedTransactionTypePicker(location) &&
                    ["Selling", "BuyingAndSelling"].includes(flowState.transactionType)
                  }
                  pageWidth={pageWidth}
                />
              </AdsConversionStep>
            ),
          },
          {
            name: "Selling sorry page",
            slug: "selling-sorry",
            valid: (flowState) => flowState.sellingRejectionReasons.length > 0,
            component: (flowState) => (
              <AdsConversionStep key="selling-sorry" step="selling-sorry">
                <QuoteSorryPage
                  mode="direct"
                  transactionType={flowState.transactionType}
                  rejectionReasons={flowState.sellingRejectionReasons}
                  rejectQuote="selling"
                />
              </AdsConversionStep>
            ),
          },
          {
            name: "Buying Quote details",
            slug: "buying-quote",
            valid: (flowState) => shouldShowBuyingQuoteDetailsForm(flowState.transactionType),
            isFirstPage: (flowState) => ["Buying"].includes(flowState.transactionType),
            component: (flowState) => (
              <AdsConversionStep key="buying-quote" step="buying-quote">
                <QuoteBuyingProperty
                  mode="direct"
                  transactionType={flowState.transactionType}
                  buyingDetails={flowState.buyingDetails}
                  defaultChannel={state.defaultChannel}
                  referral={flowState.referral}
                  referralLocked={state.referralLocked}
                  showLandingCopy={
                    userSkippedTransactionTypePicker(location) && ["Buying"].includes(flowState.transactionType)
                  }
                  pageWidth={pageWidth}
                />
              </AdsConversionStep>
            ),
          },
          {
            name: "Buying sorry page",
            slug: "buying-sorry",
            valid: (flowState) => flowState.buyingRejectionReasons.length > 0,
            component: (flowState) => (
              <AdsConversionStep key="buying-sorry" step="buying-sorry">
                <QuoteSorryPage
                  mode="direct"
                  transactionType={flowState.transactionType}
                  rejectionReasons={flowState.buyingRejectionReasons}
                  rejectQuote="buying"
                />
              </AdsConversionStep>
            ),
          },
          {
            name: "Buying mortgage lender",
            slug: "buying-mortgage-lender",
            valid: (flowState) => shouldShowBuyingMortgageLender(flowState.buyingDetails),
            component: (flowState) => <BuyingMortgageLender mode="direct" buyingDetails={flowState.buyingDetails} />,
          },
          {
            name: "Personal details",
            slug: "personal-details",
            component: (flowState) => (
              <AdsConversionStep key="personal-details" step="personal-details">
                <SaveQuoteForm
                  personalDetails={flowState.personalDetails}
                  transactionType={flowState.transactionType}
                  buyingDetails={flowState.buyingDetails || {}}
                  sellingDetails={flowState.sellingDetails || {}}
                  referral={flowState.referral}
                  referralLocked={state.referralLocked}
                  defaultChannel={state.defaultChannel}
                  defaultReferrer={state.defaultReferrer}
                  pageWidth={pageWidth}
                  googleAnalyticsId={googleAnalyticsId}
                  campaign={campaign}
                />
              </AdsConversionStep>
            ),
          },
          {
            name: "Quote details",
            slug: "your-quote",
            component: (flowState) => (
              <QuoteSubmission
                personalDetails={flowState.personalDetails}
                transactionType={flowState.transactionType}
                buyingDetails={flowState.buyingDetails || {}}
                sellingDetails={flowState.sellingDetails || {}}
                referral={flowState.referral}
                referralLocked={state.referralLocked}
                defaultChannel={state.defaultChannel}
                defaultReferrer={state.defaultReferrer}
                pageWidth={pageWidth}
                googleAnalyticsId={googleAnalyticsId}
                campaign={campaign}
                onFlowComplete={({ quoteDetails }) => {
                  const shownQuote = {
                    referral: flowState.referral,
                    buyingDetails: flowState.buyingDetails,
                    sellingDetails: flowState.sellingDetails,
                    transactionType: flowState.transactionType,
                    quoteDetails,
                  };
                  saveShownQuote(location, shownQuote);
                  setSubmittedFlowState(shownQuote);
                  setFlowCompleted(true);
                }}
              />
            ),
          },
        ]}
      />
    </PageLayout>
  );
};

export default QuoteFlow;
